import React, { useState, useEffect } from 'react';
import { Store, Plus, Minus, Trash2, ShoppingCart, CreditCard, Banknote, Check, UtensilsCrossed, ChefHat, Coffee, Printer } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

import { calculateCateringTotal, getCateringBreakdown, BASE_PRICE } from '../../utils/pricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATERING_CATEGORIES = [
  { key: 'corbalar', label: 'Corbalar' },
  { key: 'ana_yemekler', label: 'Ana Yemekler' },
  { key: 'yardimci_yemekler', label: 'Yardimci' },
  { key: 'salatalar', label: 'Salatalar' },
];

const SalonSales = ({ token, onPrint }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [cateringMenu, setCateringMenu] = useState(null);
  const [cart, setCart] = useState([]);
  const [payment, setPayment] = useState('nakit');
  const [tableNum, setTableNum] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [todaySales, setTodaySales] = useState({ total: 0, count: 0 });
  const [activeTab, setActiveTab] = useState('alakart');

  // Catering selection state (like Catering.js)
  const [cateringQty, setCateringQty] = useState({});
  const [personCount, setPersonCount] = useState(1);
  const [beverageItems, setBeverageItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/menu`).then(r => setMenuItems(r.data)).catch(() => {});
    axios.get(`${API}/catering/today`).then(r => { if (r.data) setCateringMenu(r.data); }).catch(() => {});
    axios.get(`${API}/beverages`).then(r => setBeverageItems(r.data)).catch(() => {});
    fetchTodaySales();
  }, []);

  const fetchTodaySales = () => {
    const today = new Date().toISOString().split('T')[0];
    axios.get(`${API}/admin/salon-sales?date=${today}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setTodaySales({ total: r.data.total, count: r.data.count })).catch(() => {});
  };

  // A la carte cart functions
  const addToCart = (item) => {
    setCart(prev => {
      const key = `${item.name}-${item.item_type}`;
      const step = item.item_type === 'beverage' ? 1 : 0.5;
      const existing = prev.find(c => `${c.name}-${c.item_type}` === key);
      if (existing) return prev.map(c => `${c.name}-${c.item_type}` === key ? { ...c, quantity: c.quantity + step } : c);
      return [...prev, { item_id: item.item_id || item.name, name: item.name, name_tr: item.name, price: item.price, quantity: 1, item_type: item.item_type || 'alakart', catering_selections: item.catering_selections || null }];
    });
  };

  const updateQty = (key, delta) => {
    setCart(prev => prev.map(c => {
      if (`${c.name}-${c.item_type}` !== key) return c;
      const step = c.item_type === 'beverage' ? 1 : 0.5;
      const min = c.item_type === 'beverage' ? 1 : 0.5;
      return { ...c, quantity: Math.max(min, c.quantity + delta * step) };
    }));
  };

  const removeFromCart = (key) => {
    setCart(prev => prev.filter(c => `${c.name}-${c.item_type}` !== key));
  };

  const getTotalCateringItems = () => Object.values(cateringQty).reduce((s, q) => s + q, 0);
  const calculateCateringPrice = () => calculateCateringTotal(cateringMenu, cateringQty);
  const getCateringBreakdownLocal = () => getCateringBreakdown(cateringMenu, cateringQty);

  const addCateringToCart = () => {
    const totalItems = getTotalCateringItems();
    if (totalItems < 1) { toast.error('En az 1 urun secin'); return; }
    const unitPrice = calculateCateringPrice();

    // Build selections for the order
    const selections = {};
    for (const cat of CATERING_CATEGORIES) {
      const items = (cateringMenu?.[cat.key] || []).filter(item => (cateringQty[item.id] || 0) > 0);
      if (items.length > 0) {
        selections[cat.key] = items.map(item => ({ id: item.id, name: item.name, quantity: cateringQty[item.id], price_addition: item.price_addition || 0 }));
      }
    }

    const detailParts = Object.entries(selections).map(([, items]) =>
      items.map(i => `${i.quantity}x ${i.name}`).join(', ')
    ).join(' | ');

    setCart(prev => [...prev, {
      item_id: `catering-salon-${Date.now()}`,
      name: `Gunluk Menu (${totalItems} kap)`,
      name_tr: `Gunluk Menu (${totalItems} kap)`,
      price: unitPrice,
      quantity: personCount,
      item_type: 'catering',
      catering_selections: selections,
      catering_detail: detailParts
    }]);

    toast.success(`${personCount} kisilik gunluk menu sepete eklendi (€${(unitPrice * personCount).toFixed(2)})`);
    setCateringQty({});
    setPersonCount(1);
  };

  const cartTotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);

  const handleSubmit = async () => {
    if (!cart.length) return toast.error('Sepet bos');
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/salon-sale`, {
        items: cart,
        payment_method: payment,
        table_number: tableNum ? parseInt(tableNum) : null,
        notes: notes || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Salon satisi kaydedildi! €${cartTotal.toFixed(2)}`);
      
      // Print receipt
      if (onPrint && res.data) {
        const orderForPrint = {
          id: res.data.id || res.data.order_id || `SALON-${Date.now()}`,
          user_name: 'Salon Satisi',
          user_phone: '',
          items: cart,
          total_price: cartTotal,
          payment_method: payment,
          table_number: tableNum ? parseInt(tableNum) : null,
          notes: notes || null,
          order_type: 'salon',
          created_at: new Date().toISOString(),
          status: 'completed'
        };
        onPrint(orderForPrint);
      }
      
      setCart([]);
      setTableNum('');
      setNotes('');
      fetchTodaySales();
    } catch {
      toast.error('Kayit basarisiz');
    } finally {
      setSaving(false);
    }
  };

  const filtered = menuItems.filter(m => !search || m.name.toLowerCase().includes(search.toLowerCase()));
  const categories = [...new Set(filtered.map(m => m.category))];

  const cateringByCategory = CATERING_CATEGORIES.map(cat => ({
    ...cat,
    items: (cateringMenu?.[cat.key] || []).filter(
      item => !search || item.name.toLowerCase().includes(search.toLowerCase())
    )
  })).filter(cat => cat.items.length > 0);

  const { sets: rounds, extraItems, totalItems: cateringTotal } = getCateringBreakdownLocal();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Menu Items */}
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-basil/10 rounded-full flex items-center justify-center">
            <Store className="w-5 h-5 text-basil" />
          </div>
          <div>
            <h2 className="font-playfair text-xl font-semibold text-charcoal">Salon Satis</h2>
            <p className="text-sm text-charcoal/60">Salonda satilan urunleri secin</p>
          </div>
          <div className="ml-auto bg-basil/10 rounded-xl px-4 py-2 text-right">
            <p className="text-xs text-charcoal/60">Bugun Salon</p>
            <p className="font-bold text-basil" data-testid="today-salon-total">€{todaySales.total.toFixed(2)} ({todaySales.count})</p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-2">
          <button
            onClick={() => setActiveTab('alakart')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeTab === 'alakart' ? 'bg-basil text-white' : 'bg-stone-100 text-charcoal/70 hover:bg-stone-200'}`}
            data-testid="salon-tab-alakart"
          >
            <UtensilsCrossed className="w-4 h-4" />
            A La Carte
          </button>
          <button
            onClick={() => setActiveTab('catering')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeTab === 'catering' ? 'bg-terracotta text-white' : 'bg-stone-100 text-charcoal/70 hover:bg-stone-200'}`}
            data-testid="salon-tab-catering"
          >
            <ChefHat className="w-4 h-4" />
            Gunluk Menu
          </button>
          <button
            onClick={() => setActiveTab('beverages')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeTab === 'beverages' ? 'bg-amber-700 text-white' : 'bg-stone-100 text-charcoal/70 hover:bg-stone-200'}`}
            data-testid="salon-tab-beverages"
          >
            <Coffee className="w-4 h-4" />
            Icecekler
          </button>
        </div>

        <Input
          placeholder="Urun ara..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full"
          data-testid="salon-search"
        />

        {/* A La Carte Tab */}
        {activeTab === 'alakart' && (
          <>
            {categories.map(cat => (
              <div key={cat}>
                <h3 className="font-semibold text-sm text-charcoal/60 uppercase tracking-wide mb-2 mt-4">{cat}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {filtered.filter(m => m.category === cat).map(item => (
                    <button
                      key={item.name}
                      onClick={() => addToCart({ ...item, item_type: 'alakart', item_id: item.id || item.name })}
                      className="bg-white rounded-xl p-3 text-left shadow-sm hover:shadow-md hover:bg-basil/5 transition-all border border-stone-100 active:scale-95"
                      data-testid={`salon-item-${item.name.replace(/\s+/g, '-')}`}
                    >
                      <p className="font-semibold text-charcoal text-sm truncate">{item.name}</p>
                      <p className="text-terracotta font-bold text-sm mt-1">€{item.price.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Catering Tab */}
        {activeTab === 'catering' && (
          <>
            {!cateringMenu ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <ChefHat className="w-12 h-12 text-charcoal/20 mx-auto mb-3" />
                <p className="text-charcoal/50">Bugun icin gunluk menu tanimlanmamis</p>
              </div>
            ) : (
              <>
                {/* Pricing info */}
                <div className="bg-terracotta/5 border border-terracotta/20 rounded-xl p-3 text-sm">
                  <p className="font-semibold text-charcoal">Her 4 Kategoriden 1er = {'\u20AC'}{BASE_PRICE} | 2ser = {'\u20AC'}{BASE_PRICE * 2}</p>
                  <p className="text-charcoal/60 text-xs">Ayni kategoriden ekstra = urun fiyati. 4ten az = bireysel fiyat</p>
                </div>

                {/* Category items with quantity selectors */}
                {cateringByCategory.map(cat => (
                  <div key={cat.key}>
                    <h3 className="font-semibold text-sm text-charcoal/60 uppercase tracking-wide mb-2 mt-4">{cat.label}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {cat.items.map(item => {
                        const qty = cateringQty[item.id] || 0;
                        const isSelected = qty > 0;
                        return (
                          <div
                            key={item.id}
                            className={`bg-white rounded-xl p-3 shadow-sm border transition-all ${isSelected ? 'border-terracotta bg-terracotta/5 ring-1 ring-terracotta/30' : 'border-stone-100'} ${item.stock <= 0 ? 'opacity-40' : ''}`}
                            data-testid={`salon-catering-${item.id}`}
                          >
                            <p className="font-semibold text-charcoal text-sm truncate">{item.name}</p>
                            <p className="text-xs text-charcoal/50 mt-0.5">
                              {item.price_addition > 0 ? `Ekstra: +€${item.price_addition.toFixed(2)}` : 'Set dahil'}
                            </p>
                            <div className="flex items-center justify-center gap-2 mt-2">
                              <button
                                onClick={() => setCateringQty(prev => ({ ...prev, [item.id]: Math.max(0, qty - 1) }))}
                                disabled={qty === 0}
                                className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 disabled:opacity-30"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-6 text-center font-bold text-sm">{qty}</span>
                              <button
                                onClick={() => setCateringQty(prev => ({ ...prev, [item.id]: qty + 1 }))}
                                disabled={item.stock <= 0}
                                className="w-7 h-7 rounded-full bg-terracotta/20 flex items-center justify-center hover:bg-terracotta/30 disabled:opacity-30"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Catering summary & add to cart */}
                {cateringTotal > 0 && (
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-terracotta/20 mt-4">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <div className="flex items-center gap-1 px-3 py-1 bg-basil/10 rounded-full text-sm">
                        <span className="text-charcoal/60">Secilen:</span>
                        <span className="font-bold text-basil">{cateringTotal} kap</span>
                      </div>
                      {extraItems === 0 && rounds > 0 ? (
                        <div className="px-3 py-1 bg-green-100 rounded-full text-sm text-green-700 font-medium">
                          {rounds} set = {'\u20AC'}{rounds * BASE_PRICE}
                        </div>
                      ) : extraItems > 0 ? (
                        <div className="px-3 py-1 bg-terracotta/10 rounded-full text-sm text-terracotta font-medium">
                          {rounds} set + {extraItems} ekstra
                        </div>
                      ) : null}
                      <div className="font-bold text-lg text-terracotta ml-auto">
                        €{calculateCateringPrice().toFixed(2)}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 bg-stone-100 rounded-full p-0.5">
                        <button
                          onClick={() => setPersonCount(Math.max(1, personCount - 1))}
                          className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-sm"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center font-bold text-sm">{personCount}</span>
                        <button
                          onClick={() => setPersonCount(personCount + 1)}
                          className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-sm"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <span className="text-xs text-charcoal/50 ml-1 mr-2">kisi</span>
                      </div>
                      <Button
                        onClick={addCateringToCart}
                        className="flex-1 bg-terracotta text-white hover:bg-terracotta/90 rounded-full"
                        data-testid="salon-add-catering-btn"
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Sepete Ekle (€{(calculateCateringPrice() * personCount).toFixed(2)})
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Beverages Tab */}
        {activeTab === 'beverages' && (
          <>
            {beverageItems.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <Coffee className="w-12 h-12 text-charcoal/20 mx-auto mb-3" />
                <p className="text-charcoal/50">Henuz icecek eklenmemis</p>
              </div>
            ) : (() => {
              const filteredBev = beverageItems.filter(b => !search || b.name.toLowerCase().includes(search.toLowerCase()));
              const bevCats = [...new Set(filteredBev.map(b => b.category))];
              return bevCats.map(cat => (
                <div key={cat}>
                  <h3 className="font-semibold text-sm text-charcoal/60 uppercase tracking-wide mb-2 mt-4">{cat}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {filteredBev.filter(b => b.category === cat).map(item => (
                      <button
                        key={item.id}
                        onClick={() => addToCart({ ...item, item_type: 'beverage', item_id: item.id })}
                        className="bg-white rounded-xl p-3 text-left shadow-sm hover:shadow-md hover:bg-amber-50 transition-all border border-stone-100 active:scale-95"
                        data-testid={`salon-bev-${item.id}`}
                      >
                        <p className="font-semibold text-charcoal text-sm truncate">{item.name}</p>
                        <p className="text-terracotta font-bold text-sm mt-1">€{item.price.toFixed(2)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </>
        )}
      </div>
      <div className="lg:col-span-1">
        <div className="bg-white rounded-2xl shadow-sm p-5 sticky top-4">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b">
            <ShoppingCart className="w-5 h-5 text-terracotta" />
            <h3 className="font-semibold text-charcoal">Sepet ({cart.length})</h3>
          </div>

          {cart.length === 0 ? (
            <p className="text-charcoal/40 text-center py-8 text-sm">Urun ekleyin</p>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto mb-4">
              {cart.map(c => {
                const key = `${c.name}-${c.item_type}`;
                return (
                  <div key={key} className="flex items-center gap-2 py-2 border-b border-stone-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-charcoal truncate">{c.name}</p>
                      <p className="text-xs text-charcoal/50">
                        €{c.price.toFixed(2)}
                        {c.item_type === 'catering' && <span className="ml-1 text-terracotta">(Menu)</span>}
                      </p>
                      {c.catering_detail && <p className="text-xs text-charcoal/40 truncate">{c.catering_detail}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQty(key, -1)} className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-6 text-center font-bold text-sm">{c.quantity % 1 === 0 ? c.quantity : c.quantity.toFixed(1)}</span>
                      <button onClick={() => updateQty(key, 1)} className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="font-bold text-sm w-14 text-right">€{(c.price * c.quantity).toFixed(2)}</span>
                    <button onClick={() => removeFromCart(key)} className="text-red-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t pt-3 space-y-3">
            <div className="flex justify-between font-bold text-lg">
              <span>Toplam</span>
              <span className="text-terracotta" data-testid="salon-cart-total">€{cartTotal.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Masa No" value={tableNum} onChange={e => setTableNum(e.target.value)} className="text-sm" data-testid="salon-table-num" />
              <Input placeholder="Not" value={notes} onChange={e => setNotes(e.target.value)} className="text-sm" data-testid="salon-notes" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPayment('nakit')}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${payment === 'nakit' ? 'border-green-500 bg-green-50 text-green-700' : 'border-stone-200 text-charcoal/60'}`}
                data-testid="salon-pay-cash"
              >
                <Banknote className="w-4 h-4" />Nakit
              </button>
              <button
                onClick={() => setPayment('kart')}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${payment === 'kart' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-stone-200 text-charcoal/60'}`}
                data-testid="salon-pay-card"
              >
                <CreditCard className="w-4 h-4" />Kart
              </button>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={loading || !cart.length}
              className="w-full bg-basil text-white hover:bg-basil/90 py-5 text-base font-semibold"
              data-testid="salon-submit-btn"
            >
              {loading ? 'Kaydediliyor...' : <><Check className="w-5 h-5 mr-2" />Satisi Kaydet</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalonSales;
